import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaBook } from 'react-icons/fa';
import fetchReviewers from '../../services/fetchReviewers';
import ReviewerCard from '../Reviewers/ReviewerCard';

const AdminReviewers = () => {
  const [error, setError] = useState('');
  const [username, setUser] = useState('');
  const [reviewers, setReviewers] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [courseCode, setCourseCode] = useState('');
  const [quizNumber, setQuizNumber] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reviewerToDelete, setReviewerToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 8;

  const navigate = useNavigate();

  useEffect(() => {
    const verifySession = async () => {
      const sesId = sessionStorage.getItem("ses_id");

      if (!sesId) {
        navigate('/admin');
        return;
      }

      try {
        const response = await fetch('https://website-api-ten.vercel.app/session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_API_KEY
          },
          body: JSON.stringify({ ses_id: sesId })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          sessionStorage.clear();
          navigate('/admin');
        }

        sessionStorage.setItem("username", data.username);
      } catch (error) {
        setError('An error occurred when verifying session');
      }
    };

    verifySession();
  }, [navigate]);

  const loadData = async () => {
    try {
      const paramsMap = {
        page: currentPage,
        noPerPage: itemsPerPage
      };

      if (courseCode) {
        paramsMap['data.short_name'] = courseCode;
        if (quizNumber) paramsMap['data.quiz_num'] = quizNumber;
      } else {
        setQuizNumber('');
      }

      if (academicYear) paramsMap['data.acad_year'] = academicYear;

      const queryParams = new URLSearchParams(paramsMap).toString();

      const filterURL = `https://website-api-ten.vercel.app/getFilters`;
      const filters = await fetchReviewers(filterURL);
      setFilterOptions(filters);

      const url = `https://website-api-ten.vercel.app/reviewers?${queryParams}`;
      const data = await fetchReviewers(url);
      setReviewers(data.results);
      setTotalPages(data.pagination.totalPages);

    } catch (err) {
      setError('Failed to load reviewers. Please try again later.');
    }
  };

  useEffect(() => {
    loadData();
  }, [courseCode, quizNumber, academicYear, currentPage]);

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1); // Reset to first page when filters change
    if (name === 'courseCode') setCourseCode(value);
    else if (name === 'quizNumber') setQuizNumber(value);
    else if (name === 'academicYear') setAcademicYear(value);
  };

  // Function to handle page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleModalOpen = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
  
    // Collect form data
    const formData = {
        request_by: sessionStorage.getItem('username'),
        acad_year: e.target.academicYear.value,
        full_name: e.target.fullCourseName.value,
        short_name: e.target.shortCourseName.value,
        quiz_num: e.target.coverage.value,
        links: {
            formula: e.target.linkToFormulaCard.value,
            notes: e.target.linkToNotes.value,
            reviewers: e.target.linkToReviewers.value,
        }
    };
  
    try {
      console.log(JSON.stringify(formData))
      const response = await fetch('https://website-api-ten.vercel.app/reviewers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY
        },
        body: JSON.stringify(formData),
      });
  
      if (response.ok) {
        // Handle success, e.g., close the modal or show a success message
        console.log('Reviewer added successfully');
        handleModalClose();
        await loadData()
      } else {
        // Handle error response
        console.error('Error adding reviewer');
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  };

  // Function to open delete confirmation modal
  const handleDeleteClick = (reviewer) => {
    setReviewerToDelete(reviewer);
    setIsDeleteModalOpen(true);
  };

  // Function to handle delete request
  const handleDelete = async () => {
    if (!reviewerToDelete) return;

    try {
      const response = await fetch(`https://website-api-ten.vercel.app/reviewers?rev_id=${reviewerToDelete.rev_id}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY
        }
      });

      if (response.ok) {
        await loadData();
        setIsDeleteModalOpen(false);
        setReviewerToDelete(null);
      } else {
        console.error('Error deleting reviewer');
      }
    } catch (error) {
      console.error('Network error:', error);
    }
  };
  
  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mt-4 font-header">Manage Reviewers</h1>
        <hr className="border-gray-300 mt-6 w-1/3 mx-auto" />
      </div>

      {/* Reviewers Section */}
      <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 mb-6">
        <div className="flex items-center space-x-4">
          <div className="bg-black p-4 rounded-full">
            <FaBook size={30} color="#FFF" />
          </div>
          <h2 className="text-3xl font-bold text-gray-800 font-header">Reviewers</h2>
        </div>

        {/* Add Reviewer Button */}
        <button
          onClick={handleModalOpen}
          className="inline-flex text-center bg-blue-800 text-white px-4 py-2 rounded-md hover:bg-blue-900 font-body"
        >
          Add Reviewer
        </button>
      </div>

      {/* Search Filters */}
      <div className="flex space-x-4 mb-6">
        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="courseCode"
          value={courseCode}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Course Code
          </option>
          {filterOptions.short_name && filterOptions.short_name.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="quizNumber"
          value={quizNumber}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Coverage
          </option>
          {filterOptions.quiz_num && courseCode !== '' && filterOptions.quiz_num[courseCode].map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <select
          className="border border-gray-300 rounded-md p-2 bg-white text-gray-800 w-full font-body"
          name="academicYear"
          value={academicYear}
          onChange={handleSelectChange}
        >
          <option key="" value="">
              Academic Year
          </option>
          {filterOptions.acad_year && filterOptions.acad_year.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

      </div>

      {/* List of Reviewers */}
      <div className="flex flex-wrap justify-center gap-6 mb-6">
        {reviewers?.length > 0 ? (
          reviewers.map((card, index) => (
            <div key={index} className="relative">
              <ReviewerCard card={card} />
              <button 
                onClick={() => handleDeleteClick(card)}
                className="absolute top-2 right-2 text-red-700 hover:text-red-800 focus:outline-none font-body"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          ))
        ) : (
          <p className="font-body">No reviewers found based on the selected filters.</p>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mb-6">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-2 rounded-md font-body ${
              currentPage === 1
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-800 text-white'
            }`}
          >
            Previous
          </button>
          
          <div className="flex space-x-2">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                onClick={() => handlePageChange(index + 1)}
                className={`px-4 py-2 rounded-md font-body ${
                  currentPage === index + 1
                    ? 'bg-blue-800 text-white'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-2 rounded-md font-body ${
              currentPage === totalPages
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-blue-700 hover:bg-blue-800 text-white'
            }`}
          >
            Next
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && reviewerToDelete && (
        <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-gray-900 bg-opacity-50">
          <div className="relative p-4 w-full max-w-md bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-body">Confirm Deletion</h3>
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setReviewerToDelete(null);
                }}
                className="text-gray-400 bg-transparent hover:bg-gray-200 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white font-body"
              >
                <svg className="w-3 h-3" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13" />
                </svg>
              </button>
            </div>
            <div className="p-4 md:p-5">
              <p className="mb-4 text-gray-700 dark:text-gray-300 font-body">
                Are you sure you want to delete this reviewer?
                <br />
                <span className="font-semibold font-body">
                  {reviewerToDelete.data.short_name} - {reviewerToDelete.data.quiz_num}
                </span>
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setReviewerToDelete(null);
                  }}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 font-medium rounded-lg text-sm dark:text-gray-300 dark:hover:text-white font-body"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 font-medium rounded-lg text-sm dark:bg-red-500 dark:hover:bg-red-600 font-body"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Reviewer Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex justify-center items-center w-full h-full bg-gray-900 bg-opacity-50">
          <div className="relative p-4 w-full max-w-md bg-white rounded-lg shadow dark:bg-gray-700">
            <div className="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white font-body">Add Reviewer</h3>
              <button
                onClick={handleModalClose}
                className="text-gray-400 bg-transparent hover:bg-gray-200 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white font-body"
              >
                <svg className="w-3 h-3" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 14">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleFormSubmit} className="p-4 md:p-5">
              {/* Academic Year Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Academic Year</label>
                <input type="text" name="academicYear" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" required />
              </div>
              
              {/* Full Course Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Full Course Name</label>
                <input type="text" name="fullCourseName" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" required />
              </div>

              {/* Short Course Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Short Course Name</label>
                <input type="text" name="shortCourseName" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" required />
              </div>

              {/* Coverage Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Coverage</label>
                <input type="text" name="coverage" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" required />
              </div>

              {/* Link to Reviewers Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Link to Reviewers</label>
                <input type="url" name="linkToReviewers" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" />
              </div>

              {/* Link to Notes Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Link to Notes</label>
                <input type="url" name="linkToNotes" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" />
              </div>

              {/* Link to Formula Card Field */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-white font-body">Link to Formula Card</label>
                <input type="url" name="linkToFormulaCard" className="mt-1 p-2 w-full border rounded-lg dark:bg-gray-600 dark:border-gray-500 dark:text-white font-body" />
              </div>

              {/* Submit Button */}
              <button type="submit" className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 font-body">
                Add Reviewer
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviewers;
